import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { mqUser } from 'utils/responsive';
import { FormInput } from 'ui/components/elements/FormInput/FormInput';
import { FormLabel } from 'ui/components/elements/FormLabel/FormLabel';
import { FormSelect } from 'ui/components/elements/FormSelect/FormSelect';

const ModalTitle = styled.h2`
    font-size: var(--font-large);
    font-weight: var(--font-bold);
    color: var(--main-font-color);
    margin: 0 0 10px 0;
`;

const Description = styled.p`
    font-size: var(--font-medium);
    color: #666;
    margin: 0 0 20px 0;
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    margin-bottom: 20px;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 30px;
`;

const Button = styled.button<{ primary?: boolean; disabled?: boolean }>`
    padding: 12px 24px;
    border: none;
    border-radius: 6px;
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.2s;
    opacity: ${(props) => (props.disabled ? 0.5 : 1)};

    ${(props) =>
        props.primary
            ? `
        background-color: var(--main-color-blue);
        color: var(--white);
        &:hover:not(:disabled) {
            background-color: #0056b3;
        }
    `
            : `
        background-color: #f5f5f5;
        color: var(--main-font-color);
        &:hover:not(:disabled) {
            background-color: #e0e0e0;
        }
    `}

    ${mqUser('SP')} {
        padding: 10px 16px;
        font-size: var(--font-small);
    }
`;

export interface BankAccount {
    id: number;
    bank_code: string;
    bank_name: string;
    bank_branch_code: string;
    branch_name: string;
    account_type: string;
    account_type_display: string;
    account_holder_kana: string;
}

interface BankAccountManagementProps {
    account: BankAccount | null;
    onClose: () => void;
    onSave: (account: Partial<BankAccount>) => Promise<void>;
    loading?: boolean;
}

export const BankAccountManagement: React.FC<BankAccountManagementProps> = ({
    account,
    onClose,
    onSave,
    loading = false,
}) => {
    const [bankCode, setBankCode] = useState('');
    const [bankName, setBankName] = useState('');
    const [bankBranchCode, setBankBranchCode] = useState('');
    const [branchName, setBranchName] = useState('');
    const [accountType, setAccountType] = useState('normal');
    const [accountHolderKana, setAccountHolderKana] = useState('');

    useEffect(() => {
        if (account) {
            setBankCode(account.bank_code);
            setBankName(account.bank_name);
            setBankBranchCode(account.bank_branch_code);
            setBranchName(account.branch_name);
            setAccountType(account.account_type);
            setAccountHolderKana(account.account_holder_kana);
        } else {
            // 口座が存在しない場合はフォームをリセット
            setBankCode('');
            setBankName('');
            setBankBranchCode('');
            setBranchName('');
            setAccountType('normal');
            setAccountHolderKana('');
        }
    }, [account]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await onSave({
                bank_code: bankCode,
                bank_name: bankName,
                bank_branch_code: bankBranchCode,
                branch_name: branchName,
                account_type: accountType,
                account_holder_kana: accountHolderKana,
            });
            // 成功したらモーダルを閉じる
            onClose();
        } catch (error) {
            // エラーは親コンポーネントで処理される
            console.error('口座保存エラー:', error);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <ModalTitle>入金口座情報変更</ModalTitle>
            <Description>口座番号は振込申請時に入力します</Description>

            <FormGroup>
                <FormLabel title="銀行コード" />
                <FormInput
                    type="text"
                    value={bankCode}
                    onChange={(e) => setBankCode(e.target.value)}
                    placeholder="例: 0001"
                />
            </FormGroup>

            <FormGroup>
                <FormLabel title="銀行名" isRequired />
                <FormInput
                    type="text"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    placeholder="例: みずほ銀行"
                    required
                />
            </FormGroup>

            <FormGroup>
                <FormLabel title="支店コード" />
                <FormInput
                    type="text"
                    value={bankBranchCode}
                    onChange={(e) => setBankBranchCode(e.target.value)}
                    placeholder="例: 001"
                />
            </FormGroup>

            <FormGroup>
                <FormLabel title="支店名" isRequired />
                <FormInput
                    type="text"
                    value={branchName}
                    onChange={(e) => setBranchName(e.target.value)}
                    placeholder="例: 本店"
                    required
                />
            </FormGroup>

            <FormGroup>
                <FormLabel title="口座種別" isRequired />
                <FormSelect
                    value={accountType}
                    onChange={(e) => setAccountType(e.target.value)}
                    options={[
                        { value: 'normal', label: '普通預金' },
                        { value: 'current', label: '当座預金' },
                    ]}
                    required
                />
            </FormGroup>

            <FormGroup>
                <FormLabel title="口座名義人カナ" isRequired />
                <FormInput
                    type="text"
                    value={accountHolderKana}
                    onChange={(e) => setAccountHolderKana(e.target.value)}
                    placeholder="例: ヤマダタロウ"
                    required
                />
            </FormGroup>

            <ButtonGroup>
                <Button type="button" onClick={onClose} disabled={loading}>
                    キャンセル
                </Button>
                <Button type="submit" primary disabled={loading}>
                    {loading ? '保存中...' : account ? '更新' : '登録'}
                </Button>
            </ButtonGroup>
        </form>
    );
};
